import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getLocale, useIntl } from 'umi';
import moment from 'moment';
import { Segment, Select, Dropdown, List, SearchBox } from 'tea-component';
import {
  formatExeclNormalPercent,
  formatExeclNum,
  formatNum,
  formatPercentNum,
  getChartDateFormatString,
  handleDownload,
  formatFilterValues,
} from '@/pages/dashboard/utils/utils';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import CardBox from '@/pages/dashboard/components/CardBox';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import { xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import { useWeekDay, useSelectArrObj, useSelectorMap } from '@/pages/dashboard/utils/customHook';
import useSearch from '@/pages/dashboard/components/SelectorMultiple/hooks/useSearch';
import { TABLE_CELL_PADDING_LEFT, TABLE_CELL_PADDING_RIGHT } from '@/pages/home/_config/config';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { dynamicString } from '@home/utils/dynamicString';
import dashboardRequest from '@/pages/dashboard/utils/dashboardRequest';
import {
  IConfigModeOption,
  IConfigModeTrendStepProps,
  IDropdownSizeConfig,
  IDropdownStyleConfig,
} from './_type';

/**
 * configMode 下的趋势图组件
 * 支持外部配置化的指标选项、维度选项、API 调用等
 */
const ConfigModeTrendStep: React.FC<IConfigModeTrendStepProps> = (props) => {
  const {
    gameCode, filterVal, filterLoading,
    titleConfig, segmentConfig, metricOptions, dimensionOptions,
    apiConfig, requestBuilder, dataExtractor, segmentVal, dropdownClassName,
    noStack, data, dropdownStyle,
  } = props;

  const { getWeekDay } = useWeekDay();
  const { formatMessage } = useIntl();
  const isEN = getLocale() === 'en-US';
  const timeType = 'Day';
  // 筛选器维度名称中英文翻译（参考 MetricsUserCompositionCo 的实现）
  const { getSelectorText } = useSelectorMap();

  // 解析 dropdownStyle 配置：支持全局配置或按 segment value 分别配置
  const resolveDropdownSize = (config: IDropdownStyleConfig | undefined, seg: string): IDropdownSizeConfig => {
    if (!config) return {};
    // 判断是否为按 segment 分别配置（含 segment key）
    if ((config as Record<string, IDropdownSizeConfig>)?.[seg]) {
      return (config as Record<string, IDropdownSizeConfig>)[seg];
    }
    // 如果有 buttonWidthEn/buttonWidthCn/panelMinWidth 顶层字段，视为全局配置
    const c = config as IDropdownSizeConfig;
    if (c.buttonWidthEn !== undefined || c.buttonWidthCn !== undefined || c.panelMinWidth !== undefined) {
      return c;
    }
    return {};
  };
  const isInitialMountRef = useRef<boolean>(true);

  // segment: Rate%/Count
  const defaultSegValue = segmentConfig?.defaultValue || segmentConfig?.options?.[0]?.value || 'rate';
  const [segValue, setSegValue] = useState<string>(defaultSegValue);
  const resolvedSegOptions = useMemo(() => {
    return segmentConfig?.options?.map((opt) => ({
      text: isEN ? opt.textEn : opt.textCn,
      value: opt.value,
    })) || [];
  }, [segmentConfig, isEN]);

  // 当前 segment 对应的指标选项
  const currentMetricList = useMemo(() => {
    return metricOptions?.[segValue] || [];
  }, [metricOptions, segValue]);

  // 指标下拉 - 默认选第一个带 default 标记的或第一个
  const getDefaultMetric = (list: IConfigModeOption[]): string => {
    const defaultItem = list?.find((item: IConfigModeOption) => item.default);
    return defaultItem?.value || list?.[0]?.value || '';
  };
  const [metricValue, setMetricValue] = useState<string>(() => getDefaultMetric(currentMetricList));

  // 竞态控制：每次发请求递增 fetchId，响应回来后只有最新的 fetchId 才更新数据
  const fetchIdRef = useRef<number>(0);

  // 切换 segment 时同步更新 metricValue，优先选中带 default 标记的项
  const handleSegChange = (value: string): void => {
    const newList = metricOptions?.[value] || [];
    const newDefault = getDefaultMetric(newList);
    setSegValue(value);
    setMetricValue(newDefault);
    if (newDefault) {
      fetchData(newDefault, dimensionValue, value);
    }
  };

  // 维度下拉：有 dimensionOptions 配置则用配置，否则自动从筛选器生成
  const autoFilterDimOptions = useSelectArrObj(filterVal, false, false);
  const resolvedDimOptions = useMemo(() => {
    if (dimensionOptions && dimensionOptions.length > 0) {
      return dimensionOptions.map((opt) => ({
        text: isEN ? (opt.textEn || opt.text || '') : (opt.textCn || opt.text || ''),
        value: opt.value,
        groupKey: opt.groupKey,
      }));
    }
    // 自动从筛选器生成：accumulated + separated 维度
    const accumulated = [{ value: 'accumulated', text: isEN ? 'Accumulated' : '累计值', groupKey: 'accumulated' }];
    const separated = (autoFilterDimOptions || []).filter((item: any) => item?.groupKey === 'separated');
    return [...accumulated, ...separated];
  }, [dimensionOptions, autoFilterDimOptions, isEN]);

  const defaultDimension = resolvedDimOptions?.[0]?.value || 'accumulated';
  const [dimensionValue, setDimensionValue] = useState<string>(defaultDimension);

  const resolvedMetricSelectOptions = useMemo(() => {
    return currentMetricList?.map((opt: any) => {
      const resolvedEn = opt.enName ? dynamicString(opt.enName, data, '') : '';
      const resolvedCn = opt.cnName ? dynamicString(opt.cnName, data, '') : '';
      const text = isEN
        ? (opt.textEn || resolvedEn || opt.text || '')
        : (opt.textCn || resolvedCn || opt.text || '');
      return { text, value: opt.value };
    }) || [];
  }, [currentMetricList, isEN, data?.metricsInfo]);

  // 指标搜索 hook（复用 StepSelect 的 useSearch）
  const {
    searchValue: metricSearchValue,
    setSearchValue: setMetricSearchValue,
    buildHighLightData: buildMetricHighLightData,
    handleSearchValueChange: handleMetricSearchValueChange,
  } = useSearch(resolvedMetricSelectOptions);

  const metricSearchOptions = buildMetricHighLightData() || [];

  // 当前 segment 对应的标签文本（如 Rate%/Count）
  const segLabelText = useMemo(() => {
    const seg = resolvedSegOptions?.find((opt: any) => opt.value === segValue);
    return seg?.text || segValue;
  }, [resolvedSegOptions, segValue]);

  // 首次挂载：用初始 metricValue 发起请求
  useEffect(() => {
    if (metricValue) {
      fetchData(metricValue, dimensionValue, segValue);
    }
    isInitialMountRef.current = false;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 趋势数据
  const [trendsData, setTrendsData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  // 选中指标的显示文本（优先 enName/cnName 动态字符串，回退 textEn/textCn 静态文本）
  const selectedMetricText = useMemo(() => {
    const item = currentMetricList?.find((opt: any) => opt.value === metricValue);
    if (!item) return metricValue;
    const resolvedEn = item.enName ? dynamicString(item.enName, data, '') : '';
    const resolvedCn = item.cnName ? dynamicString(item.cnName, data, '') : '';
    const en = resolvedEn || item.textEn || item.text || metricValue;
    const cn = resolvedCn || item.textCn || item.text || metricValue;
    return isEN ? en : cn;
  }, [currentMetricList, metricValue, isEN, data]);

  // 当前选中指标的 formatter（解析动态字符串后的真实值）
  const selectedMetricFormatter = useMemo(() => {
    const item = currentMetricList?.find((opt: any) => opt.value === metricValue);
    if (!item?.formatter) return '';
    return dynamicString(item.formatter, data, '');
  }, [currentMetricList, metricValue, data]);

  // 当前选中指标的 yAxisFormatter
  const selectedYAxisFormatter = useMemo(() => {
    const item = currentMetricList?.find((opt: any) => opt.value === metricValue);
    if (!item?.yAxisFormatter) return '';
    return dynamicString(item.yAxisFormatter, data, '');
  }, [currentMetricList, metricValue, data]);

  // 标题
  const title = useMemo(() => {
    if (titleConfig) {
      return isEN ? (titleConfig.titleEn || '') : (titleConfig.titleCn || '');
    }
    return '';
  }, [titleConfig, isEN]);

  // 请求数据（带竞态保护：快速切换时只有最新请求的响应才更新状态）
  const fetchData = (metric: string, dimension: string, seg: string): void => {
    if (filterLoading || !filterVal?.rangeTime?.[0]) return;

    setError(false);
    setLoading(true);

    // 递增 fetchId，标记本次请求
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;

    const handleResponse = (res: any): void => {
      // 竞态保护：只处理最新一次请求的响应
      if (currentFetchId !== fetchIdRef.current) return;
      setLoading(false);
      const resultData = Array.isArray(res) ? res : (res?.list || res?.data || []);
      setTrendsData(resultData);
    };
    const handleError = (): void => {
      if (currentFetchId !== fetchIdRef.current) return;
      setLoading(false);
      setError(true);
    };

    // 使用外部 requestBuilder 构建参数
    if (requestBuilder) {
      const params = requestBuilder({
        filterVal,
        gameCode,
        metricValue: metric,
        dimensionValue: dimension,
        segValue: seg,
        segmentVal,
      });
      if (!params) {
        setLoading(false);
        return;
      }
      const apiUrl = dimension === 'accumulated'
        ? apiConfig?.trendApi
        : apiConfig?.dimensionApi;
      if (!apiUrl) {
        setLoading(false);
        return;
      }
      dashboardRequest({ url: apiUrl, method: 'post', data: params })
        .then(handleResponse)
        .catch(handleError);
      return;
    }

    // 默认请求逻辑 - apiConfig 必须包含 trendApi / dimensionApi
    if (!apiConfig?.trendApi || !apiConfig?.dimensionApi) {
      setLoading(false);
      return;
    }
    const { rangeTime, timeType: _timeType, ...restFilterVal } = filterVal || {};
    const daysBack = apiConfig.daysBack ?? 13;
    const startTime = rangeTime?.[0] ? moment(rangeTime[0]).subtract(daysBack, 'day').format('YYYY-MM-DD') : rangeTime?.[0];
    const adFormat = segmentVal || 'interstitial';
    const formattedFilters = formatFilterValues(restFilterVal);
    // 将 ad_format 注入到 custom_filters
    const existingCustomFilters = formattedFilters?.custom_filters || [];
    const adFormatExists = existingCustomFilters.some((item: any) => item.key === 'ad_format');
    if (!adFormatExists) {
      existingCustomFilters.push({ key: 'ad_format', filter_list: [adFormat] });
    } else {
      existingCustomFilters.forEach((item: any, idx: number) => {
        if (item.key === 'ad_format') {
          existingCustomFilters[idx] = { ...item, filter_list: [adFormat] };
        }
      });
    }
    formattedFilters.custom_filters = existingCustomFilters;
    const baseParams: any = {
      game_code: gameCode,
      period: apiConfig.period || 'daily',
      start_time: startTime,
      end_time: rangeTime?.[1] || rangeTime?.[0],
      logical_flag: apiConfig.logicalFlag,
      filters: formattedFilters,
    };

    if (dimension === 'accumulated') {
      baseParams.metrics = [metric];
      dashboardRequest({ url: apiConfig.trendApi, method: 'post', data: baseParams })
        .then(handleResponse)
        .catch(handleError);
    } else {
      // 分维度请求：如果当前指标配置了 sortMetric，则用 sortMetric 作为排序指标，当前指标放入 metrics 数组
      const currentMetricItem = (metricOptions?.[seg] || []).find((item: any) => item.value === metric);
      if (currentMetricItem?.sortMetric) {
        baseParams.metric = currentMetricItem.sortMetric;
        baseParams.metrics = [metric];
      } else {
        baseParams.metric = metric;
      }
      baseParams.logical_flag = apiConfig.dimensionLogicalFlag;
      // custom 维度走 custom_accumulation，标准维度走 by_xxx
      const customAccKeys = apiConfig.customAccumulationKeys || [];
      if (customAccKeys.includes(dimension)) {
        baseParams.accumulation = {
          custom_accumulation: [{ key: dimension, is_accumulation: true }],
        };
      } else {
        baseParams.accumulation = { [`by_${dimension}`]: true };
      }
      if (apiConfig.topNum) baseParams.top_num = apiConfig.topNum;
      dashboardRequest({ url: apiConfig.dimensionApi, method: 'post', data: baseParams })
        .then(handleResponse)
        .catch(handleError);
    }
  };

  // segmentVal（广告类型）/ filterVal 变化时重新请求（跳过首次挂载，避免与初始化重复）
  useEffect(() => {
    if (isInitialMountRef.current) return;
    if (!filterLoading && filterVal?.rangeTime?.[0]) {
      fetchData(metricValue, dimensionValue, segValue);
    }
  }, [filterVal, filterLoading, segmentVal]); // eslint-disable-line react-hooks/exhaustive-deps

  // 图表数据处理
  const chartData = useMemo(() => {
    // 使用外部 dataExtractor
    if (dataExtractor) {
      return dataExtractor(trendsData, segValue, dimensionValue, metricValue);
    }

    // 默认数据处理
    if (dimensionValue === 'accumulated') {
      // 累积模式：单指标单线
      const xAxisData = trendsData?.map((item: any) => item?.date) || [];
      const values: any[] = [];
      trendsData?.forEach((item: any) => {
        const metricData = item?.metrics?.[metricValue];
        values.push(metricData?.value ?? '');
      });
      return {
        xAxisData,
        legendData: [selectedMetricText],
        lineSeries: [{ name: selectedMetricText, data: values, type: 'line', symbol: 'circle' }],
        barSeries: [{ name: selectedMetricText, data: values, type: 'bar', ...(noStack ? {} : { stack: 'bar' }), symbol: 'circle', barGap: '5%', barCategoryGap: '50%' }],
      };
    } else {
      // 分维度模式：多线
      // 参考 OverviewMetricsChart/useItem.tsx 的 getDimensionValue：
      // 优先从 custom_dimensions 数组取值（custom 维度），回退到 ${dim}_name（标准维度）
      const xAxisData = [...new Set(trendsData?.map((item: any) => item?.date) || [])];
      const getDimCode = (item: any): string => {
        // 1. 优先 custom_dimensions（version / ua_network / campaign 等）
        if (Array.isArray(item?.custom_dimensions)) {
          const hit = item.custom_dimensions.find((d: any) => d?.key === dimensionValue);
          if (hit?.value !== undefined && hit?.value !== null) return String(hit.value);
        }
        // 2. 标准维度：${dim}_name 字段（country_name / system_name 等）
        const nameField = `${dimensionValue}_name`;
        if (item?.[nameField] !== undefined && item?.[nameField] !== null && item?.[nameField] !== '') {
          return String(item[nameField]);
        }
        // 3. 兜底：直接取 dimensionValue 同名字段
        if (item?.[dimensionValue] !== undefined && item?.[dimensionValue] !== null) {
          return String(item[dimensionValue]);
        }
        return '';
      };
      const getDimDisplayName = (item: any): string => {
        const code = getDimCode(item);
        if (!code) return 'Unknown';
        return getSelectorText(dimensionValue, code);
      };
      const legendSet = new Set<string>();
      trendsData?.forEach((item: any) => {
        legendSet.add(getDimDisplayName(item));
      });
      const legendData = [...legendSet];
      const lineSeries: any[] = [];
      const barSeries: any[] = [];
      legendData.forEach((legend) => {
        const values: any[] = [];
        xAxisData.forEach((date) => {
          const item = trendsData?.find((d: any) => d?.date === date && getDimDisplayName(d) === legend);
          values.push(item?.metrics?.[metricValue]?.value ?? '');
        });
        lineSeries.push({ name: legend, data: values, type: 'line', symbol: 'circle' });
        const barItem: any = { name: legend, data: values, type: 'bar', symbol: 'circle', barGap: '5%', barCategoryGap: '50%' };
        if (!noStack && values.length > 7) barItem.stack = 'bar';
        barSeries.push(barItem);
      });
      return { xAxisData, legendData, lineSeries, barSeries };
    }
  }, [trendsData, segValue, dimensionValue, metricValue, dataExtractor, getLocale()]); // eslint-disable-line react-hooks/exhaustive-deps

  const { xAxisData, legendData, lineSeries, barSeries } = chartData;
  const isNodata = lineSeries?.filter((item: any) => item?.data?.length)?.length === 0;

  // 表格数据
  const tableOptions = useMemo(() => {
    const dateColumn = {
      key: 'date',
      align: 'left',
      fixed: 'left',
      width: 160,
      header: formatMessage({ id: 'common_date' }),
      render: (cvm: any) => {
        const weekDay = getWeekDay(cvm?.date);
        const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
        return <>
          <span className='table-text'>{getChartDateFormatString(timeType, cvm?.date, true)}</span>
          {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
        </>;
      },
    };
    const isRate = segValue === 'rate';
    // 格式化值的统一辅助函数：有 formatter 优先用 dataFormat，否则回退原有逻辑
    const fmtValue = (val: any) => {
      if (selectedMetricFormatter) return dataFormat({ value: val, formatRule: selectedMetricFormatter });
      return isRate ? formatPercentNum(val) : formatNum(parseFloat(val), false, true);
    };
    const fmtDownload = (val: any) => {
      if (selectedMetricFormatter) return dataFormat({ value: val ?? '-', formatRule: selectedMetricFormatter });
      return isRate ? formatExeclNormalPercent(val ?? '-') : formatExeclNum(val ?? '-');
    };
    if (dimensionValue === 'accumulated') {
      const tableOption = [
        dateColumn,
        {
          key: metricValue,
          align: 'right',
          width: 120 + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
          header: selectedMetricText,
        }
      ];
      const tableData: any[] = [];
      const downloadData: any[] = [];
      trendsData?.forEach((item: any) => {
        const val = item?.metrics?.[metricValue]?.value;
        const formattedDate = moment(item?.date).format('YYYY-MM-DD');
        tableData.push({ date: item?.date, [metricValue]: fmtValue(val) ?? '-' });
        downloadData.push({ date: formattedDate, [metricValue]: fmtDownload(val) });
      });
      return { table: { option: tableOption, data: tableData, downloadData } };
    } else {
      const dimColumns = legendData?.map((legend: string) => ({
        key: legend,
        align: 'right',
        width: 120 + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
        header: legend,
      }));
      const tableOption = [dateColumn, ...dimColumns];
      const tableData: any[] = [];
      const downloadData: any[] = [];
      // 与图表一致：优先 custom_dimensions，回退 ${dim}_name
      const getTableDimCode = (d: any): string => {
        if (Array.isArray(d?.custom_dimensions)) {
          const hit = d.custom_dimensions.find((cd: any) => cd?.key === dimensionValue);
          if (hit?.value !== undefined && hit?.value !== null) return String(hit.value);
        }
        const nf = `${dimensionValue}_name`;
        if (d?.[nf] !== undefined && d?.[nf] !== null && d?.[nf] !== '') return String(d[nf]);
        if (d?.[dimensionValue] !== undefined && d?.[dimensionValue] !== null) return String(d[dimensionValue]);
        return '';
      };
      const getTableDimDisplayName = (d: any): string => {
        const code = getTableDimCode(d);
        if (!code) return 'Unknown';
        return getSelectorText(dimensionValue, code);
      };
      xAxisData?.forEach((date: string) => {
        const obj: any = { date };
        const formattedDate = moment(date).format('YYYY-MM-DD');
        const downObj: any = { date: formattedDate };
        legendData?.forEach((legend: string) => {
          const item = trendsData?.find((d: any) => d?.date === date && getTableDimDisplayName(d) === legend);
          const val = item?.metrics?.[metricValue]?.value;
          obj[legend] = fmtValue(val) ?? '-';
          downObj[legend] = fmtDownload(val);
        });
        tableData.push(obj);
        downloadData.push(downObj);
      });
      return { table: { option: tableOption, data: tableData, downloadData } };
    }
  }, [trendsData, metricValue, segValue, dimensionValue, legendData, xAxisData, isEN, selectedMetricFormatter]); // eslint-disable-line react-hooks/exhaustive-deps

  // tooltip
  const configFormatterFunc = (params: any) => {
    const weekDay = getWeekDay(params[0].axisValue);
    const isRate = segValue === 'rate';
    return getEchartTooltipText({
      title: `${moment(`${params[0].axisValue}`).format('YYYY-MM-DD')}(${weekDay})`,
      metricsName: segLabelText,
      data: [...params].map(({ seriesName, value, color }) => {
        let metricsNum;
        if (selectedMetricFormatter) {
          metricsNum = dataFormat({ value, formatRule: selectedMetricFormatter });
        } else if (isRate) {
          metricsNum = formatPercentNum(value);
        } else {
          metricsNum = formatNum(parseFloat(value), false, true);
        }
        return { name: seriesName, metricsNum, color };
      }),
      isSort: true,
    });
  };

  // Y 轴格式化：优先使用配置的 yAxisFormatter/formatter，否则回退到原有 rate/bmk 逻辑
  const yAxisFormatterFn = (value: any) => {
    const yFmt = selectedYAxisFormatter || selectedMetricFormatter;
    if (yFmt) return dataFormat({ value, formatRule: yFmt });
    if (segValue === 'rate') {
      const v = parseFloat(value);
      if (isNaN(v)) return '-';
      const pct = v * 100;
      return pct === Math.floor(pct) ? `${Math.floor(pct)}%` : `${pct.toFixed(1)}%`;
    }
    return dataFormat({ value, formatRule: 'bmk' });
  };

  // 解析当前 segment 下的下拉宽度配置
  const currentDropdownSize = useMemo(() => resolveDropdownSize(dropdownStyle, segValue), [dropdownStyle, segValue]);
  const resolvedButtonWidth = isEN ? currentDropdownSize.buttonWidthEn : currentDropdownSize.buttonWidthCn;
  const resolvedPanelMinWidth = currentDropdownSize.panelMinWidth;

  const renderList = [{
    type: 'barChart',
    svgType: 'column-chart',
    content: formatMessage({ id: 'common_bar_chart' }),
    component: <BarChart
      height={360}
      legendData={xAxisData?.length > 0 ? legendData : []}
      xAxisData={xAxisData || []}
      series={xAxisData?.length > 0 ? barSeries : []}
      formatterFunc={configFormatterFunc}
      setOtherConfigFunc={(opt: any) => {
        opt.xAxis[0].axisLabel.formatter = (value: string) => getChartDateFormatString(timeType, value);
        opt.yAxis.axisLabel.formatter = yAxisFormatterFn;
        opt = xAxisHighLight(opt, {
          formatter: (p: { value: string; }) => getChartDateFormatString(timeType, p.value),
        });
        return opt;
      }}
    />
  }, {
    type: 'lineChart',
    svgType: 'discount-chart',
    content: formatMessage({ id: 'common_line_chart' }),
    component: <LineChart
      height={360}
      legendData={xAxisData?.length > 0 ? legendData : []}
      xAxisData={xAxisData || []}
      series={xAxisData?.length > 0 ? lineSeries : []}
      formatterFunc={configFormatterFunc}
      setOtherConfigFunc={(opt: any) => {
        opt.xAxis[0].axisLabel.formatter = (value: string) => getChartDateFormatString(timeType, value);
        opt.yAxis.axisLabel.formatter = yAxisFormatterFn;
        opt = xAxisHighLight(opt, {
          formatter: (p: { value: string; }) => getChartDateFormatString(timeType, p.value),
        });
        return opt;
      }}
    />
  }, {
    type: 'sheet',
    svgType: 'sheet',
    content: formatMessage({ id: 'common_table' }),
    component: <MiniTable
      height={360}
      loading={false}
      tableOption={tableOptions?.table?.option}
      tableList={tableOptions?.table?.data}
    />
  }];

  return <CardBox
    className='dbd-trends-step'
    height={360}
    bordered={false}
    titleCom={title}
    filterSlot={
      <div>
        <div className='dbd-trends-flex'>
          <Segment
            className="step-flex-segment"
            rimless={false}
            value={segValue}
            onChange={handleSegChange}
            options={resolvedSegOptions}
          />
          <Dropdown
            className={`dbd-step-select ${dropdownClassName ? `${dropdownClassName}-btn` : ''}`}
            style={resolvedButtonWidth ? { width: resolvedButtonWidth } : undefined}
            boxClassName={`dbd-step-select-dropdown ${dropdownClassName || ''}`}
            boxStyle={resolvedPanelMinWidth ? { minWidth: resolvedPanelMinWidth } : undefined}
            appearance="button"
            matchButtonWidth={!dropdownClassName && !resolvedButtonWidth}
            clickClose={false}
            onClose={() => {
              setMetricSearchValue('');
            }}
            button={
              <div className="select-button">
                <div className="select-button-text">{segLabelText}</div>
                <div className="select-button-value">{selectedMetricText}</div>
              </div>
            }
          >
            {(close: any) => (
              <>
                <SearchBox
                  placeholder={formatMessage({ id: 'selector_multiple_component_placeholder' })}
                  className="selector-multiple-search-input"
                  onChange={handleMetricSearchValueChange}
                  value={metricSearchValue}
                />
                {
                  metricSearchValue ? <List type="option">
                    {
                      metricSearchOptions?.length > 0 ?
                        metricSearchOptions?.map((item: any) => {
                          return <List.Item
                            key={item?.value}
                            className={metricValue === item?.value ? 'is-selected' : ''}
                            onClick={() => {
                              setMetricValue(item?.value);
                              fetchData(item?.value, dimensionValue, segValue);
                              close?.();
                            }}>
                            {/* 高亮文本来自配置的指标名/动态字符串解析，非用户输入；useSearch 已对搜索关键字进行 escape */}
                            <span
                              dangerouslySetInnerHTML={{ __html: String(item?.text || '') }}
                            />
                          </List.Item>;
                        }) :
                        <div className="dbd-step-select-dropdown-nodata">{formatMessage({ id: 'selector_multiple_component_no_data' })}</div>
                    }
                  </List> :
                    <List type="option">
                      {
                        resolvedMetricSelectOptions?.map((item: any) => {
                          return <List.Item
                            key={item?.value}
                            className={metricValue === item?.value ? 'is-selected' : ''}
                            onClick={() => {
                              setMetricValue(item?.value);
                              fetchData(item?.value, dimensionValue, segValue);
                              close?.();
                            }}>
                            {item?.text}
                          </List.Item>;
                        })
                      }
                    </List>
                }
              </>
            )}
          </Dropdown>
        </div>
        <Select
          listWidth={180}
          listHeight={226}
          appearance="default"
          className="overlay-line-select-text"
          options={resolvedDimOptions}
          value={dimensionValue}
          onChange={(value) => {
            setDimensionValue(value);
            fetchData(metricValue, value, segValue);
          }}
          placeholder="Accumulated"
        />
      </div>
    }
    renderList={renderList || []}
    defaultAvtive={1}
    downloadClick={async () => {
      handleDownload(tableOptions?.table, title);
    }}
    isLoading={loading}
    isError={error}
    isNodata={isNodata}
  />;
};

export default ConfigModeTrendStep;
